import React, { useState } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "./ThemedText";
import { useThemeColor } from "@/hooks/useThemeColor";

/**
 * Interface representing an individual option in a ThemedSegmentedControl.
 */
export interface ISegmentOption<T = string> {
  /** Optional display label for the segment */
  label?: string;
  /** Value representation */
  value: T;
  /** Optional icon glyph from Ionicons */
  icon?: keyof typeof Ionicons.glyphMap;
  /** Optional accessible label / hover title */
  accessibilityLabel?: string;
  /** Optional disabled state */
  disabled?: boolean;
  /** Whether the option is a destructive action (e.g. Revoke) */
  destructive?: boolean;
}

/**
 * Properties for ThemedSegmentedControl.
 */
export interface IThemedSegmentedControlProps<T = string> {
  /** List of selectable segments */
  options: ISegmentOption<T>[];
  /** Currently active value */
  value?: T;
  /** Callback when user selects a segment */
  onChange: (value: T) => void;
  /** Whether the control is locked in read-only mode */
  readOnly?: boolean;
  /** Whether the control is completely disabled */
  disabled?: boolean;
  /** Sizing variant: 'small' for tight card rows or 'medium' for standard */
  size?: "small" | "medium";
  /** Optional style for individual segment buttons */
  style?: StyleProp<ViewStyle>;
  /** Optional container style */
  containerStyle?: StyleProp<ViewStyle>;
}

/**
 * Standardised Archival Segmented Control / Segment Editor component.
 * Implements platform-wide crisp hover border, zero layout shift, and read-only shading.
 *
 * @param {IThemedSegmentedControlProps<T>} props - Component properties.
 * @returns {React.JSX.Element} The rendered segmented control.
 */
export function ThemedSegmentedControl<T = string>({
  options,
  value,
  onChange,
  readOnly = false,
  disabled = false,
  size = "medium",
  style,
  containerStyle,
}: IThemedSegmentedControlProps<T>): React.JSX.Element {
  const [hoveredValue, setHoveredValue] = useState<T | null>(null);

  const activeBg = useThemeColor({}, "tint");
  const readOnlyBg = useThemeColor({}, "inputReadOnly");
  const readOnlyBorder = useThemeColor({}, "inputReadOnlyBorder");
  const readOnlyText = useThemeColor({}, "inputReadOnlyText");
  const buttonDisabledBg = useThemeColor({}, "buttonDisabled");
  const textMuted = useThemeColor({}, "textMuted");
  const defaultText = useThemeColor({}, "text");
  const hoverBorderColor = useThemeColor({}, "buttonHoverBorder");
  const dangerColor = useThemeColor({}, "danger");

  const isLocked = readOnly || disabled;
  const isSmall = size === "small";

  return (
    <View
      style={[
        styles.container,
        isSmall && styles.containerSmall,
        readOnly && {
          backgroundColor: readOnlyBg,
          borderColor: readOnlyBorder,
        },
        containerStyle,
      ]}
    >
      {options.map((opt) => {
        const isSelected = opt.value === value;
        const isOptionDisabled = isLocked || opt.disabled;
        const isHovered = !isOptionDisabled && hoveredValue === opt.value;

        let segmentBg = "transparent";
        if (isSelected) {
          segmentBg = readOnly ? buttonDisabledBg : activeBg;
        }

        let segmentBorder = "transparent";
        if (isHovered && !isSelected) {
          segmentBorder = opt.destructive ? dangerColor : hoverBorderColor;
        }

        let textColor = defaultText;
        if (opt.destructive) {
          textColor = dangerColor;
        } else if (isSelected) {
          textColor = readOnly ? readOnlyText : "#1C1917";
        } else if (isOptionDisabled) {
          textColor = textMuted;
        }

        const iconSize = isSmall ? 11 : 12;
        const itemTitle = opt.accessibilityLabel || opt.label || String(opt.value);

        return (
          <TouchableOpacity
            key={String(opt.value)}
            disabled={isOptionDisabled}
            activeOpacity={0.7}
            onPress={() => onChange(opt.value)}
            onHoverIn={() => !isOptionDisabled && setHoveredValue(opt.value)}
            onHoverOut={() => setHoveredValue(null)}
            accessibilityLabel={itemTitle}
            {...(Platform.select({
              web: {
                onMouseEnter: () => !isOptionDisabled && setHoveredValue(opt.value),
                onMouseLeave: () => setHoveredValue(null),
                onPointerEnter: () => !isOptionDisabled && setHoveredValue(opt.value),
                onPointerLeave: () => setHoveredValue(null),
                title: itemTitle,
              } as any,
              default: {},
            }))}
            style={[
              styles.segmentBtn,
              isSmall && styles.segmentBtnSmall,
              {
                backgroundColor: segmentBg,
                borderColor: segmentBorder,
              },
              isHovered && styles.segmentBtnHovered,
              style,
            ]}
          >
            {opt.icon && (
              <Ionicons
                name={opt.icon}
                size={iconSize}
                color={textColor}
                style={opt.label ? styles.segmentIconWithLabel : styles.segmentIconOnly}
              />
            )}
            {opt.label ? (
              <ThemedText
                type="stamp"
                style={[
                  styles.segmentText,
                  isSmall && styles.segmentTextSmall,
                  {
                    color: textColor,
                    fontWeight: isSelected ? "700" : "500",
                    opacity: isOptionDisabled && !isSelected ? 0.35 : 1,
                  },
                ]}
              >
                {opt.label}
              </ThemedText>
            ) : null}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(150, 150, 150, 0.1)",
    borderRadius: 0,
    padding: 2,
    borderWidth: 1,
    borderColor: "rgba(150, 150, 150, 0.15)",
  },
  containerSmall: {
    padding: 1,
  },
  segmentBtn: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 0,
    borderWidth: 1,
    borderColor: "transparent",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    transitionProperty: "border-color, background-color",
    transitionDuration: "150ms",
  } as any,
  segmentBtnSmall: {
    paddingVertical: 2,
    paddingHorizontal: 5,
    minWidth: 22,
  },
  segmentBtnHovered: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  segmentIconWithLabel: {
    marginRight: 4,
  },
  segmentIconOnly: {
    marginHorizontal: 1,
  },
  segmentText: {
    fontSize: 11,
    letterSpacing: 0.5,
  },
  segmentTextSmall: {
    fontSize: 9.5,
    letterSpacing: 0.2,
  },
});

